import type {
  DsdEditorCompanyNameReference,
  DsdEditorValueMatch,
} from "@/apis/dsd-editor/route";
import { countCompanyNamesNeedingUnification } from "@/utils/dsd-editor/company-names";
import type { DsdEditorParsedItem } from "@/utils/dsd-editor/document-items";
import {
  getDsdEditorSumMismatchCells,
  getDsdEditorSumValidationCells,
} from "@/utils/dsd-editor/sum-validation";

export type DsdEditorReviewMode =
  | "note-reference-review"
  | "note-note-matching"
  | "sum-validation"
  | "corrections"
  | "company-name-review";

export type DsdReviewRailCounts = Record<DsdEditorReviewMode, number>;
export type DsdReviewRailAvailability = Record<DsdEditorReviewMode, boolean>;

/**
 * rail 배지 숫자 —
 * 매칭 후보 수(본문·주석, 주석·주석), 합계 불일치 셀 수, 교정 항목 수(분석 끝난 뒤), 통일 필요 기업명 수.
 * 기준을 바꾸려면 여기만.
 */
export const getDsdReviewRailCounts = ({
  displayItems,
  valueMatches,
  noteValueMatches,
  correctionCount,
  correctionReady,
  companyNameReferences,
}: {
  displayItems: DsdEditorParsedItem[];
  valueMatches: DsdEditorValueMatch[];
  noteValueMatches: DsdEditorValueMatch[];
  correctionCount: number;
  correctionReady: boolean;
  companyNameReferences: DsdEditorCompanyNameReference[];
}): DsdReviewRailCounts => ({
  "note-reference-review": valueMatches.length,
  "note-note-matching": noteValueMatches.length,
  "sum-validation": getDsdEditorSumMismatchCells(
    getDsdEditorSumValidationCells(displayItems),
  ).length,
  corrections: correctionReady ? correctionCount : 0,
  "company-name-review": countCompanyNamesNeedingUnification(
    companyNameReferences,
  ),
});

/**
 * rail 에서 고를 수 있는 모드 — 분석 데이터가 없는 모드는 고르지 못하게 한다.
 */
export const getDsdReviewRailAvailability = ({
  displayItems,
  noteValueMatches,
  correctionCount,
  correctionReady,
  companyNameReferences,
}: {
  displayItems: DsdEditorParsedItem[];
  noteValueMatches: DsdEditorValueMatch[];
  correctionCount: number;
  correctionReady: boolean;
  companyNameReferences: DsdEditorCompanyNameReference[];
}): DsdReviewRailAvailability => ({
  "note-reference-review": true,
  "note-note-matching": noteValueMatches.length > 0,
  "sum-validation": getDsdEditorSumValidationCells(displayItems).length > 0,
  corrections: correctionReady && correctionCount > 0,
  "company-name-review": companyNameReferences.length > 0,
});
